import {
  Client,
  Form,
  FormCategory,
  FormStatusCode,
  FormTemplate,
  getStatusOptions,
} from '@nrwl/rx-models';
import {
  RxFormsBrowserComponent,
  RxListCompositeComponent,
  RxReactiveFormCompositeComponent,
  RxTextCompositeComponent,
  RxVideoCompositeComponent,
} from '@nrwl/rx-forms';
import { rxEmailValidator, rxRequiredValidator } from '@nrwl/rx-validators';

import { DialogsConfigService } from '../dialogs-config.service';
import { FormsValidatorsService } from './forms-validators.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RxCheckboxComponent } from '@nrwl/rx-checkbox';
import { RxCreateFormDialogComponent } from '@nrwl/rx-form-components';
import { RxRadioGroupComponent } from '@nrwl/rx-radio-group';
import { RxUploadBrandedComponent } from 'libs/rx-components/src/lib/rx-forms/rx-upload-branded/rx-upload-branded.component';
import { RxUploadPdfFormComponent } from 'libs/rx-components/src/lib/rx-forms/rx-upload-pdf-form/rx-upload-pdf-form.component';
import { Validators } from '@angular/forms';
import { rxNumericCheckValidator } from '@nrwl/rx-validators';
import { RxSelectComponent } from '@nrwl/rx-select';

@Injectable({
  providedIn: 'root',
})
export class FormsDialogConfigService extends DialogsConfigService {
  constructor(public formsValidatorsService: FormsValidatorsService) {
    super();
  }

  public AlertNotUniqueEmail() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'This email is already registered with our system',
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public SelectCategory(items$: Observable<FormCategory[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Select Folder',
        items$: items$,
        buttons: this.SubmitDismissButtons('Select'),
      },
    };
  }

  public FillSign() {
    return {
      component: RxFormsBrowserComponent,
      payload: {
        title: 'Select',
        buttons: this.SubmitDismissButtons('Select'),
      },
    };
  }

  public NewBasicForm() {
    return null;
  }

  public NewFormGroup(categories: FormCategory[]) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'New Form Folder',
        placeholder: 'New Folder',
        tip: 'All forms must belong to a form folder',
        tipClose: false,
        buttons: this.SubmitDismissButtons(),
        fields: [
          {
            placeholder: 'New Folder',
            labelText: null,
            hasInlineInfo: true,
            value: '',
            maxLength: 100,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(100)],
            // RWE-3427 - Removing Category Uniqueness validator
            // asyncValidators: [this.formsValidatorsService.categoryUniqValidator(categories)]
          },
        ],
      },
    };
  }

  public CloneAccountCategory(formCategory: FormCategory, allCategories: FormCategory[]) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Clone the Form Folder?',
        placeholder: 'Folder name',
        tip: 'Existing forms will be copied to the new form folder.',
        fields: [
          {
            placeholder: 'Folder name',
            maxLength: 50,
            labelText: null,
            hasInlineInfo: false,
            value: formCategory.name,
            name: 'formGroup',
            async: true,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
            asyncValidators: [this.formsValidatorsService.categoryUniqValidator(allCategories)],
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public RenameCategory(categoryName: string, categories: FormCategory[]) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Do you want to rename the folder?',
        placeholder: 'Intake form',
        tip: 'Folders can be used for bulk sharing of forms or filtering. Common strategies are to name a folder based on form type, such as consent, or, appointment type such as botox.',
        fields: [
          {
            placeholder: 'Intake form',
            maxLength: 100,
            labelText: null,
            hasInlineInfo: false,
            value: categoryName,
            name: 'intakeForm',
            async: true,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(100)],
            // RWE-3427 - Removing Category Uniqueness validator
            // asyncValidators: [this.formsValidatorsService.accountLibraryCategoryUniqueValidator(categories)]
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public RenameGlobalCategory(categoryName, categories) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Do you want to rename the folder?',
        placeholder: 'Intake form',
        tip: 'Folders can be used for bulk sharing of forms or filtering. Common strategies are to name a folder based on form type, such as consent, or, appointment type such as botox.',
        fields: [
          {
            placeholder: 'Intake form',
            maxLength: 100,
            labelText: null,
            hasInlineInfo: false,
            value: categoryName,
            name: 'intakeForm',
            async: true,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(100)],
            // RWE-3427 - Removing Category Uniqueness validator
            // asyncValidators: [this.formsValidatorsService.categoryUniqValidator(categories)]
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public RenameFormTemplate(templateName) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Do you Want to Rename the Template Form?',
        placeholder: 'Intake form',
        tip: 'All forms are catalogued by title, providers can filter forms by these titles.',
        labelText: null,
        hasInlineInfo: false,
        maxLength: 30,
        validate: true,
        fields: [
          {
            placeholder: 'Form Folder',
            labelText: null,
            hasInlineInfo: true,
            value: templateName,
            maxLength: 50,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public ChangeFormTemplateStatus(status?: number, options: { includeOngoingPlan?: boolean } = {}) {
    return {
      component: null,
      disableClose: false,
      payload: {
        title: 'Select new status',
        tip: 'Forms designated as "Complete" are final.',
        fields: [
          {
            component: RxRadioGroupComponent,
            value: status,
            items: getStatusOptions({
              includeOngoingPlan: Boolean(options.includeOngoingPlan),
            }),
            validators: [rxRequiredValidator],
          },
          {
            component: RxCheckboxComponent,
            title: 'I understand',
          },
        ],
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
      },
    };
  }

  public WarningChangeStatusPendingForm() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Warning:',
        tip: 'Completing this action makes the form(s) unavailable to the client.',
        buttons: this.SubmitDismissButtons('OK', 'Cancel'),
      },
    };
  }

  public AlertFormsCantBeFiltered() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Filters available only on Client Library.',
        buttons: this.DismissButton('OK'),
      },
    };
  }

  public AlertFormIsDeleted() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Form is not found or archived.',
        buttons: this.DismissButton('Ok'),
      },
    };
  }

  public AlertBundleFormsAreDeleted() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Some forms are not found or archived. <br>Please refine your selection and try again',
        buttons: this.DismissButton('Ok'),
      },
    };
  }

  public AlertFormIsSubmissionPending() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'After submitting, please re-share selected forms to make them accessible by client',
        buttons: this.DismissButton('Ok'),
      },
    };
  }

  public AlertFormsCantBeFilled() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'PDF forms can be filled in RxPhoto mobile app only.',
        buttons: this.DismissButton(),
      },
    };
  }

  public AlertFormsCantBeShared() {
    const tip = 'PDF forms are not shareable. Please refine your selection and try again.';
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip,
        buttons: this.DismissButton(),
      },
    };
  }

  public AlertShareCompletedForms() {
    const tip =
      'Completing this action makes forms available to the client in RxPortal for read only.';
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip,
        buttons: this.DismissButton(),
      },
    };
  }

  public AlertFormsShared() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Selected forms with "Complete" status will be available for read only. The client can fill & sign all other forms.',
        buttons: this.SubmitDismissButtons('OK', 'Cancel'),
      },
    };
  }

  public AlertCompleteFormsShared() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Completing this action makes forms available to the client for read only.',
        buttons: this.SubmitDismissButtons('OK', 'Cancel'),
      },
    };
  }

  public AlertNotCompleteFormsShared() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Sharing selected forms will change their form status to "Submission Pending". The client can  fill & sign these forms.',
        buttons: this.SubmitDismissButtons('OK', 'Cancel'),
      },
    };
  }

  public AlertFormsShareUnshare(tip) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: tip,
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public ShareFormViaPortal(tip) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: tip,
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }
  public ShareFormViaLink(tip) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: tip,
        buttons: this.SubmitDismissCustomSubButtons('Confirm', 'Cancel', 'Update Phone'),
      },
    };
  }

  public AlertSelectedShareAndUnshareForms() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Your selection contains both shared and unshared forms. Please refine your selection and try again',
        buttons: this.DismissButton('Okay'),
      },
    };
  }

  public AlertFormsUnshare(tip) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: tip,
        buttons: this.SubmitDismissButtons('Okay', 'Cancel'),
      },
    };
  }

  public AlertMissingForSharing(type: 'email' | 'phone', existingPhoneNumber?: any) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: type === 'email' ? 'Email Required' : 'Mobile Phone Required',
        tip:
          type === 'email'
            ? `Please enter client email to share form(s) via RxPortal`
            : 'Please enter client mobile phone to share form(s) via SMS',
        fields: [
          {
            placeholder: type === 'email' ? 'Email' : 'Phone',
            labelText: null,
            value: existingPhoneNumber,
            hasInlineInfo: true,
            componentType: type === 'email' ? 'input' : 'phone',
            maxLength: type === 'email' ? 50 : 20,
            validators:
              type === 'email'
                ? [
                    rxRequiredValidator,
                    rxEmailValidator,
                    Validators.minLength(2),
                    Validators.maxLength(50),
                  ]
                : [
                    rxRequiredValidator,
                    rxNumericCheckValidator,
                    Validators.minLength(2),
                    Validators.maxLength(20),
                  ],
          },
        ],
        buttons: this.SubmitDismissButtons('Share', 'Cancel'),
      },
    };
  }

  public Print() {
    return {};
    // return {
    //     component: RxPdfCompositeComponent,
    //     payload: {
    //         title: 'print',
    //         tip: 'TODO: Explanatory message for RWE-181',
    //         buttons: this.DismissButton(),
    //         print: formTemplate, // TODO: Do we need this? Rename/consolidate it
    //         name: formTemplate.name // TODO: Do we need this? Rename/consolidate it
    //     }
    // };
  }

  public Download() {
    return {
      title: 'download',
      tip: 'TODO: Explanatory message for RWE-180',
      buttons: this.DismissButton(),
    };
  }

  public UploadForm() {
    return {
      width: '768px',
      hideHeader: true,
      component: RxUploadBrandedComponent,
      payload: {
        title: 'Upload Form',
        data: null,
        type: 0,
        buttons: this.SubmitDismissButtons('Create'),
        fields: [],
      },
    };
  }

  public AddForm() {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Add Sample Template to your Account Library',
        tip: `Templates provided by RxPhoto are sample only. After adding the sample to your Account Library, please modify them to satisfy your clinical and legal requirements.`,
        fields: [
          {
            title: 'I understand',
            value: false,
            component: RxCheckboxComponent,
            validators: [Validators.requiredTrue],
          },
        ],
        buttons: this.SubmitDismissButtons('Yes', 'Cancel'),
      },
    };
  }

  public CreateFormTypePicker() {
    return {
      component: RxCreateFormDialogComponent,
      width: '407px',
      minWidth: '0',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      disableClose: false,
      hideHeader: true,
      hideFooter: true,
      panelClass: 'create-form-type-dialog',
      payload: {
        title: 'Create a New Form',
        isRxStaff: false,
        fields: [{ value: 2 }, { value: 0 }, { value: null }, { value: '' }],
        buttons: {},
      },
    };
  }

  public CloneForm(formCategory: FormCategory, formTemplates: FormTemplate[]) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Clone',
        tip: `Clone ${formTemplates.length} form(s) to '${formCategory.name}'?`,
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public PreventChangeCompleteStatus() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Status of form(s) designated as "Complete" cannot be changed',
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public AddFormToSeparateLocation(
    items$: Observable<FormCategory[]>,
    loading$?: Observable<boolean>,
  ) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Add to',
        items$: items$,
        loading$: loading$,
        buttons: this.SubmitDismissButtons('Add'),
      },
    };
  }

  public CloneFormToSeparateLocation(
    items$: Observable<FormCategory[]>,
    loading$?: Observable<boolean>,
  ) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Clone to',
        items$: items$,
        loading$: loading$,
        buttons: this.SubmitDismissButtons('Clone'),
      },
    };
  }

  public SaveToAccountLibrary(items$: Observable<FormCategory[]>, loading$?: Observable<boolean>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Save to Account Library',
        items$: items$,
        loading$: loading$,
        resultRequired: true,
        buttons: this.SubmitDismissButtons('Save'),
      },
    };
  }

  public SaveToAccountLibraryConfirmation(
    formCategory: FormCategory,
    formTemplates: FormTemplate[],
  ) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirmation',
        tip: `Save ${formTemplates.length} form(s) to '${formCategory.name}'?`,
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public MoveForm(formCategory: FormCategory, formTemplates: FormTemplate[]) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Move Templates',
        tip: `Move ${formTemplates.length} form(s) to '${formCategory.name}'?`,
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public MoveFormToSeparateLocation(
    items$: Observable<FormCategory[]>,
    loading$?: Observable<boolean>,
  ) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Move Template(s)',
        items$: items$,
        loading$: loading$,
        selectedItem: null,
        buttons: this.SubmitDismissButtons('Move'),
      },
    };
  }

  public ArchiveForms(template?: FormTemplate, selectedCount?: number) {
    let message;
    if (template && template.name) {
      message = `Are you sure that you want to archive '${template.name}' template?`;
    } else {
      const formCount = selectedCount || 0;
      const formWord = formCount === 1 ? 'template' : 'templates';
      message = `Are you sure that you want to archive ${formCount} ${formWord}?`;
    }

    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Archive Selected Form(s)?',
        tip: 'All archived forms will be stored in the Archived section. ' + message,
        buttons: this.SubmitDismissButtons('Archive'),
      },
    };
  }

  public ArchiveForm(amount: number) {
    const regexp = new RegExp(`${amount}{1}`);
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Archive Selected Form(s)?',
        tip: `You are about to archive selected form(s). Type "${amount}" into the text field to confirm archiving.`,
        placeholder: '',
        labelText: null,
        validate: true,
        fields: [
          {
            placeholder: '',
            labelText: null,
            value: '',
            maxLength: amount.toString().length,
            validators: [
              rxRequiredValidator,
              Validators.maxLength(amount.toString().length),
              Validators.pattern(regexp),
            ],
          },
        ],
        buttons: this.SubmitDismissButtons('Archive'),
      },
    };
  }

  public ArchiveCategories() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Archive Folder and Templates(s)',
        tip: 'All archived templates then will be stored in "Archived" section.',
        buttons: this.SubmitDismissButtons('Archive'),
      },
    };
  }

  public PublishTemplate() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Publish Template',
        tip: 'Are you sure to publish this Template(s)?',
        buttons: this.SubmitDismissButtons('Publish'),
      },
    };
  }

  public StopPublishingTemplate() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Stop Publishing Template',
        tip: 'Are you sure to unpublish this Template(s)?',
        buttons: this.SubmitDismissButtons('Stop Publishing'),
      },
    };
  }

  public HelpCenter() {
    return {
      component: RxVideoCompositeComponent,
      payload: {
        title: 'Help Center',
        buttons: this.DismissButton(),
      },
    };
  }

  public MultipleCategoryTypesError() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'You cannot select templates from both libraries.',
        buttons: this.DismissButton(),
      },
    };
  }

  public DeselectOnCollapseConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Form(s) selected in this folder will be deselected by this action',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public DeselectAllConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Deselect Form(s)',
        tip: 'All selected forms will be deselected by this action.',
        buttons: this.SubmitDismissButtons('Yes', 'No'),
      },
    };
  }

  public RestoreConfirmation(template: FormTemplate) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Restore Form?',
        tip:
          'The form will be restored to the initial folder by default if it exists in Account Library. ' +
          'Otherwise, it will be placed into the folder of your choice.',
        buttons: this.SubmitDismissButtons('Restore'),
        value: template.name,
        id: template.id,
      },
    };
  }

  public RestoreToGlobalLibraryConfirmation(template: FormTemplate) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Restore Form?',
        tip:
          'The form will be restored to the initial folder by default if it exists in Global Library. ' +
          'Otherwise, it will be placed into the folder of your choice.',
        buttons: this.SubmitDismissButtons('Restore'),
        value: template.name,
        id: template.id,
      },
    };
  }

  public RestoreGlobalConfirmation(template: FormTemplate) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Restore Form?',
        tip: 'The form will be restored into the folder of your choice.',
        buttons: this.SubmitDismissButtons('Restore'),
        value: template.name,
        id: template.id,
      },
    };
  }

  public RestorePracticeFormTemplatesConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Restore Templates(s)?',
        tip: 'Would you like to restore Template(s)?',
        buttons: this.SubmitDismissButtons('Restore'),
      },
    };
  }

  public Import() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Import Your Existing Forms (Intake, Consent, Charting, Survey)',
        tip: 'TODO: Explanatory message for RWE-193',
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public Warning() {
    return {
      title: 'Warning:',
      tip: 'TODO: There can be at least 3 columns',
    };
  }

  public DisableFillSign() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'PDF forms can be filled in RxPhoto mobile app only.',
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public DisableFillSignOnCompletedForm() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Complete forms are read-only and cannot be edited.',
        buttons: this.DismissButton('Dismiss'),
      },
    };
  }

  public AlertCustomizePlanUnavailable(status?: number | string | null) {
    const isComplete = Number(status) === FormStatusCode.Complete;

    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: isComplete
          ? 'Completed Personalized Plans cannot be customized.'
          : "You don't have permission to customize this Personalized Plan.",
        buttons: this.DismissButton('Dismiss'),
      },
    };
  }

  public AlertFormActionUnavailable(message: string) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: message,
        buttons: this.DismissButton('Dismiss'),
      },
    };
  }

  public AlertFormAmendmentLocked(message: string) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: message,
        buttons: this.DismissButton('Leave Editor'),
      },
    };
  }

  public OnDestroyInboxWithSelectedItems() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Selected form(s) will be deselected by this action.',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public DisableModifyCataloguedDate(data) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Modify Catalogued Date',
        data,
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [],
      },
    };
  }

  public CarryForward(form: Form, patient: Client) {
    return {
      component: RxTextCompositeComponent,
      width: '420px',
      payload: {
        title: 'Carry Forward',
        formTitle: form?.title,
        sourcePublicationDate: form?.publication_date,
        patientRemoteId: patient?.remote_id,
        data: null,
        resultRequired: true,
        skipDirtyCheck: true,
        buttons: this.SubmitDismissButtons('Carry Forward', 'Cancel'),
        fields: [],
      },
    };
  }

  public WarningPermissionEmployee() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Warning',
        tip: 'You must have Manager or Administrator permission status to access these areas. If you are seeing this message and feel you should have access, please contact with your administrator and request change in permissions. ',
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public WarningPermissionManager() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Warning',
        tip: 'You must have Administrator permission status to access these areas. If you are seeing this message and feel you should have access, please contact with your administrator and request change in permissions.',
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public ConfirmDiscardChangesAL() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'You are about to exit Fill & Sign mode with unsaved changes. Please ensure that you press "Next" on each form to save updates.',
        buttons: this.SubmitDismissButtons('Exit Fill & Sign', 'Cancel'),
      },
    };
  }

  public ConfirmDiscardChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Discard Changes',
        tip: 'You have unsaved changes. Are you sure you want to leave edit mode?',
        buttons: this.SubmitDismissButtons('Discard', 'Cancel'),
      },
    };
  }

  /**
   * Matches the approved AI Scribe leave-warning design. This is the sole confirmation
   * when an active Scribe session is left, including unsaved form data.
   */
  public ConfirmCancelScribeAndLeave() {
    return {
      component: RxTextCompositeComponent,
      width: '412px',
      panelClass: 'scribe-leave-dialog',
      hideDialogClose: true,
      headerClasses: 'scribe-leave-dialog-header',
      bodyClasses: 'scribe-leave-dialog-body',
      footerClasses: 'scribe-leave-dialog-footer',
      payload: {
        title: 'Scribe in Progress',
        tip: 'You currently have a Scribe recording in progress.<br />Leaving this form will cancel this Scribe and delete any changes made to this form.<br /><br />Are you sure you wish to leave this form?',
        buttons: this.SubmitDismissButtons('Yes, Cancel Scribe', 'Continue Recording'),
      },
    };
  }

  public ConfirmDiscardChangesCL() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Discard Changes',
        tip: 'You are about to exit Fill & Sign mode with unsaved forms. Please ensure that you press "Next" on each form to save them to the Client Library.',
        buttons: this.SubmitDismissButtons('Exit Fill & Sign', 'Cancel'),
      },
    };
  }

  public UploadPdfForm() {
    return {
      component: RxUploadPdfFormComponent,
      hideHeader: true,
      hideFooter: true,
      payload: {
        statuses: [
          { value: 2, text: 'Incomplete' },
          { value: 3, text: 'Director Review' },
          { value: 4, text: 'Supervisor Review' },
          { value: 5, text: 'Complete' },
        ],
        buttons: this.SubmitDismissButtons('Create'),
        fields: [
          {
            title: 'Upload PDF',
            value: {
              file: null,
              fileName: null,
            },
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
            buttons: this.SubmitDismissButtons('Add'),
          },
          {
            title: 'Upload PDF',
            value: {
              title: null,
              date: null,
              status: 5,
            },
            buttons: this.SubmitDismissButtons('Add'),
          },
        ],
      },
    };
  }

  public AddBasicReport() {
    return {
      title: 'Add Basic Report',
      tip: `<span>The <b>Basic Report</b> allows providers to create a simple form with up to 6 Yes or No questions and a single
        client signature. Multiple signatures are NOT available on basic forms. If you want a form that has more functionality or
        multiple signatures please reniew RxPhoto Global Library or manually send us your forms and we will create them for you.
        </span>
        <a>Watch Forms Management Tutorial.</a>`,
      buttons: this.SubmitDismissButtons(),
    };
  }

  public RequiredFieldsAlert(errorName?: string) {
    const errorMessages = {
      matDatepickerParse: 'Please enter a valid date.',
      numericOnly: 'Please enter valid numeric values for Time, Quantity, and Unit.',
    };

    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: errorMessages[errorName] ?? 'Please complete all required fields.',
        buttons: this.DismissButton('OK'),
      },
    };
  }

  public ScribeMicrophoneAccessAlert() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Unable to access microphone',
        tip: 'Unable to access microphone.',
        buttons: this.DismissButton('OK'),
      },
    };
  }

  public ConfirmDeleteFields() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Delete field',
        tip: 'Are you sure you want to delete this field?',
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public SelectAppointmentDialog(patientName: string, appointments: any[]) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Select an Appointment',
        tip: `An appointment is required to share forms with ${patientName}. Please select the appointment/service you'd like to associate this form pack with.`,

        fields: [
          {
            component: RxSelectComponent,
            placeholder: 'Select Appointment',
            selectType: 'appointment',
            value: null,
            items: appointments,
            validators: [],
            asyncValidators: [],
          },
        ],

        buttons: this.SubmitDismissButtons('Next', 'Cancel'),
      },
    };
  }

  public NoFutureAppointments(patientName: string) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Alert',
        tip: `You must schedule a future appointment for ${patientName} before sharing their forms. Please add an upcoming appointment in PatientNow and try again`,
        buttons: this.DismissButton('Close'),
      },
    };
  }

  public PatientNotSynced() {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Alert',
        tip: `This patient record isn't synced with PatientNow yet. To share this form, please sync the patient record first. Check for duplicate records and merge if needed`,
        buttons: this.DismissButton('Close'),
      },
    };
  }

  public WarningNoAppointments(count: number, total: number, names: string) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `${count} of ${total} Patient Don't Have an Appointment`,
        tip: `You must schedule a future appointment for the following patients before sharing their forms.<br><br><b>${names}</b><br><br>Please add an upcoming appointment in PatientNow and try again`,
        buttons: this.SubmitDismissButtons('Continue', 'Close'),
      },
    };
  }

  public WarningShareFormsIssues(
    notSyncedCount: number,
    noAppointmentsCount: number,
    total: number,
    notSyncedNames: string | null,
    noAppointmentsNames: string | null,
    canContinue: boolean,
  ) {
    const count = notSyncedCount + noAppointmentsCount;
    const noun = count === 1 ? 'Patient' : 'Patients';
    const sections: string[] = [];

    if (notSyncedNames) {
      sections.push(
        `This patient record isn't synced with PatientNow yet. To share this form, please sync the patient record first. Check for duplicate records and merge if needed.<br><br><b>${notSyncedNames}</b>`,
      );
    }

    if (noAppointmentsNames) {
      sections.push(
        `You must schedule a future appointment for the following patients before sharing their forms.<br><br><b>${noAppointmentsNames}</b><br><br>Please add an upcoming appointment in PatientNow and try again`,
      );
    }

    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `${count} of ${total} ${noun} Can't Be Shared`,
        tip: sections.join('<br><br>'),
        buttons: canContinue
          ? this.SubmitDismissButtons('Continue', 'Close')
          : this.DismissButton('Close'),
      },
    };
  }

  public SelectAppointmentSequenceDialog(
    patientName: string,
    appointments: any[],
    index: number,
    total: number,
    buttonLabel = 'Next',
    selectedAppointmentId: any = null,
  ) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: `Select Appointment ${index} of ${total}`,

        subtitle: `For <b>${patientName}</b>`,

        tip: `An appointment is required to share forms. Please select the patient's future appointment from the dropdown.`,
        skipDirtyCheck: true,

        fields: [
          {
            component: RxSelectComponent,
            placeholder: 'Select Appointment',
            selectType: 'appointment',
            value: selectedAppointmentId,
            items: appointments,
            validators: [rxRequiredValidator],
            asyncValidators: [],
          },
        ],
        buttons: this.SubmitDenyButtons(buttonLabel, 'Back'),
      },
    };
  }
}
